import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { format } from 'date-fns'
import { setModal, selectModal } from '../../../redux/modalSlice'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { handleErrorResponse } from '../../../helpers/errorHandling'
import {
  DEFAULT_MAX_DAYS_AHEAD,
  callbackClockNow,
  customerCalendarDate,
  formatCallbackReadBack,
  formatCurrentTimeIn,
  isSelectableCallbackTime,
} from '../../../helpers/callbackHours'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { selectSimulatedDate } from '../../../redux/simulatedDateSlice'
import Loading from '../../../components/Loading'

const MODAL_TYPE = 'schedule_callback_modal'
const GENERIC_ERROR_MESSAGE = 'There was an error. Please try again.'

const departmentSelectStyles = {
  container: (base) => ({ ...base, width: '220px' }),
}

const timezoneSelectStyles = {
  container: (base) => ({ ...base, minWidth: '220px' }),
  menu: ({ ...css }) => ({ ...css, width: 'max-content', minWidth: '100%' }),
  option: (base) => ({ ...base, whiteSpace: 'nowrap' }),
}

export default function ScheduleCallbackModal() {
  const dispatch = useDispatch()
  const modal = useSelector(selectModal)
  const simulatedDate = useSelector(selectSimulatedDate)?.simulated_date
  const infoRequest = useRequest()
  const saveRequest = useRequest()
  const removeRequest = useRequest()
  const customerId = modal.options?.customerId
  const isOpen = modal.is_active && modal.type === MODAL_TYPE

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors },
  } = useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [info, setInfo] = useState(null)
  const [confirmClear, setConfirmClear] = useState(false)
  const [now, setNow] = useState(() => callbackClockNow(simulatedDate))

  const selectedCallbackAt = watch('callback_at')
  const selectedTimezone = watch('callback_timezone')
  const selectedDepartment = watch('dialer_queue_dropdown_option_id')

  // Keeps the per-zone "current time" labels live while the agent is on the call.
  // A spoofed date stays fixed, matching the API's non-ticking SystemClock.
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }
    setNow(callbackClockNow(simulatedDate))
    const timer = setInterval(
      () => setNow(callbackClockNow(simulatedDate)),
      60 * 1000
    )
    return () => clearInterval(timer)
  }, [isOpen, simulatedDate])

  useEffect(() => {
    if (isOpen && customerId) {
      getCallbackInfo()
    }
  }, [customerId, modal.is_active])

  const prefillPendingCallback = (data) => {
    const pending = data.pending_callback
    const [year, month, day] = pending.callback_date.split('-').map(Number)
    const [hour, minute] = pending.callback_time.split(':').map(Number)
    const department = data.departments.find(
      (o) => o.id === pending.dialer_queue_dropdown_option_id
    )
    setValue(
      'callback_timezone',
      data.timezones.find((o) => o.value === pending.callback_timezone)
    )
    // Wall-clock parts only; they are read in the selected zone, not the browser's.
    setValue('callback_at', new Date(year, month - 1, day, hour, minute))
    setValue(
      'dialer_queue_dropdown_option_id',
      department ? { label: department.name, value: department.id } : null
    )
  }

  const getCallbackInfo = async () => {
    setLoading(true)
    infoRequest.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/callback`
    )
    try {
      const response = await infoRequest.get()
      const data = response?.data?.data ?? null
      setInfo(data)
      if (data?.pending_callback) {
        prefillPendingCallback(data)
      } else {
        const defaultTimezone = data?.timezones?.find(
          (o) => o.value === data?.default_timezone
        )
        if (defaultTimezone) {
          setValue('callback_timezone', defaultTimezone)
        }
      }
    } catch (error) {
      notifyError(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    reset()
    setInfo(null)
    setSaving(false)
    setConfirmClear(false)
    dispatch(setModal({ is_active: false }))
  }

  const notifyError = (error) => {
    const status = error?.response?.status
    if (!status || status >= 500) {
      dispatch(setNotification({ type: 'error', body: GENERIC_ERROR_MESSAGE }))
      return
    }
    const {
      callback_date: dateErrors,
      callback_time: timeErrors,
      ...fieldErrors
    } = error.response.data?.errors ?? {}
    // The API validates date and time separately; the form has one combined picker.
    const callbackAtErrors = [...(dateErrors ?? []), ...(timeErrors ?? [])]
    handleErrorResponse(
      callbackAtErrors.length
        ? { ...fieldErrors, callback_at: callbackAtErrors }
        : fieldErrors,
      setError,
      false
    )
    const errorMessage =
      error.response.data?.message ?? error.response.data?.messageExtreme
    dispatch(
      setNotification({
        type: 'error',
        body: errorMessage ?? GENERIC_ERROR_MESSAGE,
      })
    )
  }

  const handleClear = async () => {
    if (!confirmClear) {
      setConfirmClear(true)
      return
    }
    setSaving(true)
    removeRequest.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/callback/${info.pending_callback.id}`
    )
    try {
      await removeRequest.delete()
      modal.options?.successCallback?.()
      handleCancel()
      dispatch(
        setNotification({ type: 'success', body: 'Callback time cleared' })
      )
    } catch (error) {
      setSaving(false)
      setConfirmClear(false)
      notifyError(error)
    }
  }

  const onSubmit = async (formData) => {
    const editingId = info?.pending_callback?.id ?? null
    setSaving(true)
    saveRequest.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/callback${
        editingId ? `/${editingId}` : ''
      }`
    )
    const payload = {
      callback_date: format(formData.callback_at, 'yyyy-MM-dd'),
      callback_time: format(formData.callback_at, 'HH:mm'),
      callback_timezone: formData.callback_timezone.value,
      dialer_queue_dropdown_option_id:
        formData.dialer_queue_dropdown_option_id.value,
    }
    try {
      const response = editingId
        ? await saveRequest.modify(payload)
        : await saveRequest.post(payload)
      if (response.status === 200 || response.status === 201) {
        modal.options?.successCallback?.()
        handleCancel()
        dispatch(
          setNotification({
            type: 'success',
            body: editingId ? 'Callback updated' : 'Callback scheduled',
          })
        )
      } else {
        setSaving(false)
        dispatch(
          setNotification({ type: 'error', body: `Error: ${response.status}` })
        )
      }
    } catch (error) {
      setSaving(false)
      notifyError(error)
    }
  }

  if (!isOpen) {
    return null
  }

  const pending = info?.pending_callback
  const departmentOptions = (info?.departments ?? []).map((o) => ({
    label: o.name,
    value: o.id,
  }))
  const zipTimezone = info?.timezones?.find(
    (o) => o.value === info?.default_timezone
  )
  const isZipTimezoneOverridden =
    Boolean(zipTimezone && selectedTimezone) &&
    selectedTimezone.value !== zipTimezone.value
  const zipLabel = info?.zip_code
    ? `Customer's zip code ${info.zip_code}`
    : "Customer's zip code"
  const zipOverrideWarning =
    zipTimezone?.value === 'America/Phoenix'
      ? `${zipLabel} is in Arizona, where most areas don't change clocks for daylight saving (the Navajo Nation does and uses Mountain time). Confirm with the customer's current time before changing it.`
      : `${zipLabel} is in ${zipTimezone?.label}. Confirm with the customer's current time before changing it.`
  const readBack =
    selectedCallbackAt && selectedTimezone
      ? formatCallbackReadBack(
          selectedCallbackAt,
          selectedCallbackAt,
          selectedTimezone.value
        )
      : null
  const customerTimeTooltip = `This is the customer's time${
    selectedTimezone ? ` (${selectedTimezone.label})` : ''
  }, not your time. Enter the time the customer asked to be called.`

  const timezoneOptionLabel = (option, { context }) => {
    if (context === 'menu') {
      return `${option.label} — ${formatCurrentTimeIn(option.value, now)}`
    }
    return option.label
  }

  // Mirrors the API checks so a typed value is caught before Save; the API stays authoritative.
  const validateCallbackAt = (value) => {
    const timezone = selectedTimezone?.value
    if (!value || !timezone) {
      return true
    }
    const maxDaysAhead = info?.max_days_ahead ?? DEFAULT_MAX_DAYS_AHEAD
    const callbackDay = new Date(
      value.getFullYear(),
      value.getMonth(),
      value.getDate()
    )
    if (callbackDay > customerCalendarDate(timezone, maxDaysAhead, now)) {
      return `Callbacks can be scheduled up to ${maxDaysAhead} days ahead`
    }
    if (!isSelectableCallbackTime(value, value, timezone, now)) {
      return 'No Agents are available to return your call at this time. Please select another time within our Hours of Operation'
    }
    return true
  }

  const filterTime = (time) =>
    Boolean(selectedTimezone) &&
    isSelectableCallbackTime(time, time, selectedTimezone.value, now)

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      id="schedule_callback_modal_form"
      data-testid="schedule-callback-modal"
    >
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-5">
          <header className="modal-card-head py-3">
            <p
              className="modal-card-title"
              data-testid="schedule-callback-modal-title"
            >
              Specific Callback Time
            </p>
            <button
              type="button"
              className="delete"
              aria-label="close"
              data-testid="schedule-callback-modal-close"
              onClick={handleCancel}
            ></button>
          </header>
          <section className="modal-card-body" style={{ minHeight: '110px' }}>
            {(loading || !info) && <Loading />}
            {!loading && info && (
              <>
                <p className="mb-4">
                  Set a specific date, time, and department for this
                  customer&apos;s callback. This drives dialer queue placement
                  automatically.
                </p>
                {pending && (
                  <p
                    className="is-size-7 has-text-grey mb-3"
                    data-testid="schedule-callback-modal-current-callback"
                  >
                    Current callback: {pending.callback_datetime_customer} (
                    {pending.callback_datetime_system} Arizona system time) ·{' '}
                    {pending.department}
                  </p>
                )}

                <p className="has-text-grey is-size-7 mb-1">Department</p>
                <div data-testid="schedule-callback-modal-department">
                  <Controller
                    name="dialer_queue_dropdown_option_id"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        aria-label="Department"
                        options={departmentOptions}
                        isSearchable={false}
                        classNames={reactSelectClassNames()}
                        menuPosition="fixed"
                        styles={departmentSelectStyles}
                        form="schedule_callback_modal_form"
                      />
                    )}
                  />
                </div>
                <p
                  className="has-text-danger is-size-7"
                  data-testid="schedule-callback-modal-department-error"
                >
                  {errors.dialer_queue_dropdown_option_id && (
                    <span>
                      {errors.dialer_queue_dropdown_option_id.message ||
                        'Department is required'}
                    </span>
                  )}
                </p>

                <p className="has-text-grey is-size-7 mt-3 mb-1">
                  Callback Date &amp; Time
                  <span
                    style={{ pointerEvents: 'auto' }}
                    className="lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal"
                    data-tooltip={customerTimeTooltip}
                    aria-label={customerTimeTooltip}
                    data-testid="schedule-callback-modal-customer-time-tooltip"
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="pl-1 has-text-link"
                    />
                  </span>
                </p>
                <div className="is-flex is-align-items-center">
                  <div
                    className="mr-2"
                    data-testid="schedule-callback-modal-datetime"
                  >
                    <Controller
                      name="callback_at"
                      control={control}
                      rules={{ required: true, validate: validateCallbackAt }}
                      render={({ field }) => (
                        <DatePicker
                          selected={field.value ?? null}
                          onChange={(value) => field.onChange(value)}
                          showTimeSelect
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat="MM/dd/yyyy | h:mm aa"
                          minDate={
                            selectedTimezone
                              ? customerCalendarDate(
                                  selectedTimezone.value,
                                  0,
                                  now
                                )
                              : now
                          }
                          maxDate={
                            selectedTimezone
                              ? customerCalendarDate(
                                  selectedTimezone.value,
                                  info.max_days_ahead ?? DEFAULT_MAX_DAYS_AHEAD,
                                  now
                                )
                              : null
                          }
                          filterTime={filterTime}
                          disabled={!selectedTimezone}
                          placeholderText="mm/dd/yyyy | h:mm aa"
                          className="input is-small"
                          portalId="lox-datepicker-portal"
                          popperPlacement="bottom-start"
                          popperClassName="lox-datepicker-over-modal"
                        />
                      )}
                    />
                  </div>
                  <div data-testid="schedule-callback-modal-timezone">
                    <Controller
                      name="callback_timezone"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          aria-label="Time Zone"
                          options={info.timezones}
                          formatOptionLabel={timezoneOptionLabel}
                          isSearchable={false}
                          classNames={reactSelectClassNames()}
                          onChange={(val) => {
                            field.onChange(val)
                            if (
                              selectedCallbackAt &&
                              !isSelectableCallbackTime(
                                selectedCallbackAt,
                                selectedCallbackAt,
                                val.value
                              )
                            ) {
                              setValue('callback_at', null)
                            }
                          }}
                          menuPosition="fixed"
                          styles={timezoneSelectStyles}
                          form="schedule_callback_modal_form"
                        />
                      )}
                    />
                  </div>
                </div>
                <p
                  className="has-text-danger is-size-7"
                  data-testid="schedule-callback-modal-datetime-error"
                >
                  {errors.callback_at && (
                    <span>
                      {errors.callback_at.message ||
                        'Callback date and time is required'}
                    </span>
                  )}
                  {errors.callback_timezone && (
                    <span>
                      {' '}
                      {errors.callback_timezone.message ||
                        'Time zone is required'}
                    </span>
                  )}
                </p>
                <p
                  className="is-size-7 has-text-grey is-italic mt-1"
                  data-testid="schedule-callback-modal-timezone-hint"
                >
                  {info.zip_location
                    ? `Time zone defaulted from customer's mailing address (${info.zip_location}) — change if needed.`
                    : 'Select the time zone the customer is in.'}
                  {selectedTimezone && (
                    <>
                      {' '}
                      Customer&apos;s current time:{' '}
                      <strong data-testid="schedule-callback-modal-current-time">
                        {formatCurrentTimeIn(selectedTimezone.value, now)}
                      </strong>{' '}
                      — confirm with the customer.
                    </>
                  )}
                </p>
                {isZipTimezoneOverridden && (
                  <p
                    className="is-size-7 mt-1 has-text-warning-dark"
                    data-testid="schedule-callback-modal-zip-warning"
                  >
                    {zipOverrideWarning}
                  </p>
                )}

                <div
                  className="notification is-info is-light mt-4 py-3 px-4 lox-callback-info-box"
                  data-testid="schedule-callback-modal-info-box"
                >
                  {readBack ? (
                    <>
                      Until <strong>{readBack.customer}</strong>{' '}
                      {selectedTimezone.label} ({readBack.system} Arizona system
                      time), this customer is removed from the Dialer Queue. At
                      the scheduled time, they&apos;re automatically moved into
                      the <strong>High Priority Dialer</strong> queue for the{' '}
                      {selectedDepartment?.label ?? 'selected'} department.
                    </>
                  ) : (
                    <>
                      Until the scheduled time, this customer is removed from
                      the Dialer Queue. At the scheduled time, they&apos;re
                      automatically moved into the{' '}
                      <strong>High Priority Dialer</strong> queue for the
                      selected department.
                    </>
                  )}
                </div>
              </>
            )}
          </section>
          <footer className="modal-card-foot is-justify-content-space-between py-3">
            <div>
              {pending && (
                <button
                  type="button"
                  className="button is-small is-white has-text-danger px-2"
                  data-testid="schedule-callback-modal-clear"
                  disabled={saving}
                  onClick={handleClear}
                >
                  {confirmClear
                    ? 'Click again to clear callback'
                    : 'Clear Callback Time'}
                </button>
              )}
            </div>
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white"
                data-testid="schedule-callback-modal-cancel"
                onClick={handleCancel}
              >
                Cancel
              </button>
              {info && (
                <button
                  type="submit"
                  className="button is-link is-small"
                  data-testid="schedule-callback-modal-save"
                  disabled={saving}
                >
                  {saving ? 'Saving' : 'Save'}
                </button>
              )}
            </div>
          </footer>
        </div>
      </div>
    </form>
  )
}
